/**
 * BetterWild LF Landing Page (ctr-lf) — carousels + FAQ accordion
 * Mirrors the ctr-hp.js interaction patterns (scroll-snap carousel, accordion)
 * scoped to data-ctr-lf-* attributes.
 */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function initCarousels(root) {
    root.querySelectorAll('[data-ctr-lf-carousel]').forEach(function (carousel) {
      if (carousel.dataset.ctrLfCarouselReady === '1') return;
      carousel.dataset.ctrLfCarouselReady = '1';

      var track = carousel.querySelector('[data-ctr-lf-carousel-track]');
      var slides = carousel.querySelectorAll('[data-ctr-lf-carousel-slide]');
      var prev = carousel.querySelector('[data-ctr-lf-carousel-prev]');
      var next = carousel.querySelector('[data-ctr-lf-carousel-next]');
      var dotsWrap = carousel.querySelector('[data-ctr-lf-carousel-dots]');
      if (!track || !slides.length) return;

      var index = 0;
      var dots = [];

      function maxScrollLeft() {
        return Math.max(0, track.scrollWidth - track.clientWidth);
      }

      function slideScrollLeft(slide) {
        return slide.offsetLeft - track.offsetLeft;
      }

      function indexFromScroll() {
        var maxScroll = maxScrollLeft();
        if (maxScroll <= 0) return 0;
        // When several slides fit on screen, scrollLeft never reaches the last
        // slide's offsetLeft — treat near-end scroll as the last index.
        if (track.scrollLeft >= maxScroll - 2) {
          return slides.length - 1;
        }
        var closest = 0;
        var best = Infinity;
        slides.forEach(function (slide, i) {
          var dist = Math.abs(slideScrollLeft(slide) - track.scrollLeft);
          if (dist < best) {
            best = dist;
            closest = i;
          }
        });
        return closest;
      }

      function updateControls() {
        var atStart = index <= 0;
        var atEnd = index >= slides.length - 1;

        if (prev) {
          prev.classList.toggle('is-inactive', atStart);
          prev.classList.toggle('is-active', !atStart);
          prev.setAttribute('aria-disabled', atStart ? 'true' : 'false');
          prev.disabled = atStart;
        }

        if (next) {
          next.classList.toggle('is-inactive', atEnd);
          next.classList.toggle('is-active', !atEnd);
          next.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
          next.disabled = atEnd;
        }

        dots.forEach(function (dot, di) {
          dot.classList.toggle('is-active', di === index);
          dot.setAttribute('aria-current', di === index ? 'true' : 'false');
        });
      }

      function goTo(i, options) {
        index = Math.max(0, Math.min(slides.length - 1, i));
        var slide = slides[index];
        var instant = options && options.instant;
        var target = Math.min(slideScrollLeft(slide), maxScrollLeft());
        track.scrollTo({
          left: target,
          behavior: instant ? 'auto' : 'smooth',
        });
        updateControls();
      }

      if (dotsWrap) {
        slides.forEach(function (_, i) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'ctr-lf-carousel__dot' + (i === 0 ? ' is-active' : '');
          dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
          if (i === 0) dot.setAttribute('aria-current', 'true');
          dot.addEventListener('click', function () {
            goTo(i);
          });
          dotsWrap.appendChild(dot);
          dots.push(dot);
        });
      }

      if (prev) {
        prev.addEventListener('click', function () {
          if (prev.classList.contains('is-inactive')) return;
          goTo(index - 1);
        });
      }
      if (next) {
        next.addEventListener('click', function () {
          if (next.classList.contains('is-inactive')) return;
          goTo(index + 1);
        });
      }

      var scrollTimer;
      track.addEventListener('scroll', function () {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(function () {
          index = indexFromScroll();
          updateControls();
        }, 60);
      });

      updateControls();

      carousel.ctrLfCarousel = {
        goTo: goTo,
        getIndex: function () {
          return index;
        },
      };
    });
  }

  function initComplexes(root) {
    root.querySelectorAll('[data-ctr-lf-complexes]').forEach(function (shell) {
      if (shell.dataset.ctrLfComplexesReady === '1') return;
      shell.dataset.ctrLfComplexesReady = '1';

      var tabs = shell.querySelectorAll('[data-ctr-lf-complex-tab]');
      var panels = shell.querySelectorAll('[data-ctr-lf-complex-panel]');
      var mobileMq = window.matchMedia('(max-width: 749px)');
      var activeKey = null;

      function isMobile() {
        return mobileMq.matches;
      }

      function clearBodyHeights() {
        panels.forEach(function (panel) {
          var body = panel.querySelector('[data-ctr-lf-complex-body]');
          if (body) body.style.height = '';
        });
      }

      function setActive(key, options) {
        if (!key) return;
        var opts = options || {};
        activeKey = key;
        var mobile = isMobile();

        tabs.forEach(function (tab) {
          var active = tab.getAttribute('data-ctr-lf-complex-tab') === key;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
        });

        panels.forEach(function (panel) {
          var active = panel.getAttribute('data-ctr-lf-complex-panel') === key;
          panel.classList.toggle('is-active', active);
          var trigger = panel.querySelector('[data-ctr-lf-complex-trigger]');
          if (trigger) trigger.setAttribute('aria-expanded', active ? 'true' : 'false');
          var body = panel.querySelector('[data-ctr-lf-complex-body]');

          if (mobile) {
            panel.removeAttribute('hidden');
            if (body) body.style.height = active ? body.scrollHeight + 'px' : '0px';
          } else {
            if (active) panel.removeAttribute('hidden');
            else panel.setAttribute('hidden', '');
            if (body) body.style.height = '';
          }
        });

        if (opts.scroll) {
          var activePanel = shell.querySelector('[data-ctr-lf-complex-panel="' + key + '"]');
          if (activePanel) scrollBlockIntoView(activePanel);
        }
      }

      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          setActive(tab.getAttribute('data-ctr-lf-complex-tab'));
        });
      });

      panels.forEach(function (panel) {
        var trigger = panel.querySelector('[data-ctr-lf-complex-trigger]');
        if (!trigger) return;
        trigger.addEventListener('click', function () {
          var key = panel.getAttribute('data-ctr-lf-complex-panel');
          var isOpen = panel.classList.contains('is-active');
          if (isOpen && isMobile()) {
            panel.classList.remove('is-active');
            trigger.setAttribute('aria-expanded', 'false');
            var body = panel.querySelector('[data-ctr-lf-complex-body]');
            if (body) body.style.height = '0px';
            activeKey = null;
            return;
          }
          setActive(key);
        });
      });

      function onBreakpointChange() {
        clearBodyHeights();
        if (activeKey) setActive(activeKey);
        else {
          var initial = shell.querySelector('[data-ctr-lf-complex-panel].is-active');
          if (initial) setActive(initial.getAttribute('data-ctr-lf-complex-panel'));
        }
      }

      if (typeof mobileMq.addEventListener === 'function') {
        mobileMq.addEventListener('change', onBreakpointChange);
      } else if (typeof mobileMq.addListener === 'function') {
        mobileMq.addListener(onBreakpointChange);
      }

      var initial = shell.querySelector('[data-ctr-lf-complex-panel].is-active');
      if (initial) setActive(initial.getAttribute('data-ctr-lf-complex-panel'));

      shell.ctrLfComplexes = { open: setActive };
    });
  }

  function initFaq(root) {
    root.querySelectorAll('[data-ctr-lf-faq]').forEach(function (list) {
      if (list.dataset.ctrLfFaqReady === '1') return;
      list.dataset.ctrLfFaqReady = '1';

      function closeAll() {
        list.querySelectorAll('[data-ctr-lf-faq-item]').forEach(function (sib) {
          sib.classList.remove('is-active');
          var btn = sib.querySelector('[data-ctr-lf-faq-trigger]');
          if (btn) btn.setAttribute('aria-expanded', 'false');
          var body = sib.querySelector('[data-ctr-lf-faq-body]');
          if (body) body.style.height = '0px';
        });
      }

      function openItem(item) {
        if (!item || !list.contains(item)) return;
        closeAll();
        item.classList.add('is-active');
        var trigger = item.querySelector('[data-ctr-lf-faq-trigger]');
        if (trigger) trigger.setAttribute('aria-expanded', 'true');
        var body = item.querySelector('[data-ctr-lf-faq-body]');
        if (body) body.style.height = body.scrollHeight + 'px';
      }

      list.querySelectorAll('[data-ctr-lf-faq-item].is-active [data-ctr-lf-faq-body]').forEach(function (body) {
        body.style.height = body.scrollHeight + 'px';
      });

      list.addEventListener('click', function (e) {
        var trigger = e.target.closest('[data-ctr-lf-faq-trigger]');
        if (!trigger || !list.contains(trigger)) return;

        e.preventDefault();
        var item = trigger.closest('[data-ctr-lf-faq-item]');
        if (!item) return;

        if (item.classList.contains('is-active')) {
          closeAll();
          return;
        }
        openItem(item);
      });

      list.ctrLfFaq = {
        open: openItem,
        closeAll: closeAll,
      };
    });
  }

  function initModals(root) {
    root.querySelectorAll('[data-ctr-lf-modal]').forEach(function (modal) {
      if (modal.dataset.ctrLfModalReady === '1') return;
      modal.dataset.ctrLfModalReady = '1';

      function closeModal() {
        if (typeof modal.close === 'function') modal.close();
        else modal.removeAttribute('open');
        document.documentElement.classList.remove('ctr-lf-modal-open');
      }

      function openModal() {
        if (typeof modal.showModal === 'function') modal.showModal();
        else modal.setAttribute('open', '');
        document.documentElement.classList.add('ctr-lf-modal-open');
      }

      modal.querySelectorAll('[data-ctr-lf-modal-close]').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          closeModal();
        });
      });

      modal.addEventListener('click', function (e) {
        if (e.target === modal) closeModal();
      });

      modal.addEventListener('cancel', function () {
        document.documentElement.classList.remove('ctr-lf-modal-open');
      });

      modal.ctrLfModal = { open: openModal, close: closeModal };
    });
  }

  function bindModalTriggers(root) {
    root.querySelectorAll('[data-ctr-lf-modal-open]').forEach(function (trigger) {
      if (trigger.dataset.ctrLfModalTriggerReady === '1') return;
      trigger.dataset.ctrLfModalTriggerReady = '1';

      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        var id = trigger.getAttribute('data-ctr-lf-modal-open');
        if (!id) return;
        var modal = document.querySelector('[data-ctr-lf-modal="' + id + '"]');
        if (modal && modal.ctrLfModal) modal.ctrLfModal.open();
      });
    });
  }

  function initAll(root) {
    initCarousels(root);
    initComplexes(root);
    initFaq(root);
    initModals(root);
    bindModalTriggers(root);
  }

  function scrollBlockIntoView(el) {
    if (!el || typeof el.scrollIntoView !== 'function') return;
    try {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    } catch (err) {
      el.scrollIntoView(true);
    }
  }

  function onBlockSelect(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var slide =
      target.matches && target.matches('[data-ctr-lf-carousel-slide]')
        ? target
        : target.closest('[data-ctr-lf-carousel-slide]');
    if (slide) {
      var carousel = slide.closest('[data-ctr-lf-carousel]');
      if (carousel && carousel.ctrLfCarousel) {
        var slides = carousel.querySelectorAll('[data-ctr-lf-carousel-slide]');
        var index = Array.prototype.indexOf.call(slides, slide);
        if (index >= 0) {
          carousel.ctrLfCarousel.goTo(index, { instant: true });
        }
      }
      scrollBlockIntoView(slide);
      return;
    }

    var faqItem =
      target.matches && target.matches('[data-ctr-lf-faq-item]')
        ? target
        : target.closest('[data-ctr-lf-faq-item]');
    if (faqItem) {
      var faqList = faqItem.closest('[data-ctr-lf-faq]');
      if (faqList && faqList.ctrLfFaq) {
        faqList.ctrLfFaq.open(faqItem);
      }
      scrollBlockIntoView(faqItem);
      return;
    }

    var complexPanel =
      target.matches && target.matches('[data-ctr-lf-complex-panel]')
        ? target
        : target.closest('[data-ctr-lf-complex-panel]');
    if (!complexPanel) {
      complexPanel = target.closest('[data-ctr-lf-complexes] [class*="ctr-lf-complexes__ing"]');
      if (complexPanel) complexPanel = complexPanel.closest('[data-ctr-lf-complex-panel]');
    }
    if (complexPanel) {
      var shell = complexPanel.closest('[data-ctr-lf-complexes]');
      var key = complexPanel.getAttribute('data-ctr-lf-complex-panel');
      if (shell && shell.ctrLfComplexes && key) {
        shell.ctrLfComplexes.open(key, { scroll: true });
      }
      scrollBlockIntoView(complexPanel);
    }
  }

  function onSectionLoad(event) {
    var section = event.target;
    if (!section) return;
    initAll(section);
  }

  ready(function () {
    initAll(document);

    document.addEventListener('shopify:block:select', onBlockSelect);
    document.addEventListener('shopify:section:load', onSectionLoad);
  });
})();
